import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import CampaignsCard from '../partials/campaigns/CampaignsCard';
import CampaignsSkeleton from "../common/CampaignsSkeleton";
import { useUser, sortCampaignsByCreatedAt } from '../userContext';
import { normalizeCampaignStatus } from '../data/campaignStatus';
import { fetchMultipleGameStats } from '../services/gameAnalytics';
import { PlusIcon } from '@heroicons/react/24/solid';
import ScrollableTabBar from '../components/ScrollableTabBar';

/** Same as userContext so we know when to show "Load more" */
const CAMPAIGNS_INITIAL_LIMIT = 20;
const LOAD_MORE_PAGE_SIZE = 20;

const STATUS_FILTER_ALL = 'all';
const STATUS_FILTER_DRAFT = 'draft';
const STATUS_FILTER_PUBLISHED = 'published';
const STATUS_FILTER_PAUSED = 'paused';
const STATUS_FILTER_COMPLETED = 'completed';

const SORT_BY_CREATED = 'created';
const SORT_BY_PLAYS = 'plays';

export default function FullWidthGrid() {
  const [campaigns, setCampaigns] = useState([]);
  const [moreCampaigns, setMoreCampaigns] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL); // all = draft + published + paused (completed hidden)
  const [sortBy, setSortBy] = useState(SORT_BY_CREATED);
  const [playCountsMap, setPlayCountsMap] = useState({}); // campaignId -> { totalPlays, ... }
  const navigate = useNavigate();
  const { user, businessProfile, campaigns: contextCampaigns, campaignsReady } = useUser();

  const baseCampaigns = Array.isArray(contextCampaigns) ? contextCampaigns : campaigns;
  const allCampaigns = sortCampaignsByCreatedAt([...baseCampaigns, ...moreCampaigns]);
  const displayCampaigns = useMemo(() => {
    return allCampaigns.filter((c) => {
      const status = normalizeCampaignStatus(c.status);
      if (statusFilter === STATUS_FILTER_ALL) return status !== 'completed';
      return status === statusFilter;
    });
  }, [allCampaigns, statusFilter]);

  const displayCampaignIds = useMemo(() => displayCampaigns.map((c) => c.id).join(','), [displayCampaigns]);

  // Fetch play counts when sorting by plays
  useEffect(() => {
    if (sortBy !== SORT_BY_PLAYS || displayCampaigns.length === 0) return;
    let cancelled = false;
    const ids = displayCampaigns.map((c) => c.id);
    fetchMultipleGameStats(ids).then((map) => {
      if (!cancelled) setPlayCountsMap(map);
    });
    return () => { cancelled = true; };
  }, [sortBy, displayCampaigns.length, displayCampaignIds]);

  const sortedDisplayCampaigns = useMemo(() => {
    if (sortBy === SORT_BY_CREATED) return sortCampaignsByCreatedAt(displayCampaigns);
    return [...displayCampaigns].sort(
      (a, b) => (playCountsMap[b.id]?.totalPlays ?? 0) - (playCountsMap[a.id]?.totalPlays ?? 0)
    );
  }, [displayCampaigns, sortBy, playCountsMap]);

  const statusCounts = useMemo(() => {
    const counts = { [STATUS_FILTER_ALL]: 0, [STATUS_FILTER_DRAFT]: 0, [STATUS_FILTER_PUBLISHED]: 0, [STATUS_FILTER_PAUSED]: 0, [STATUS_FILTER_COMPLETED]: 0 };
    allCampaigns.forEach((c) => {
      const status = normalizeCampaignStatus(c.status);
      if (status !== 'completed') counts[STATUS_FILTER_ALL] += 1;
      if (status in counts) counts[status] += 1;
    });
    return counts;
  }, [allCampaigns]);
  const canLoadMore =
    hasMore &&
    businessProfile?.id &&
    (baseCampaigns.length === CAMPAIGNS_INITIAL_LIMIT || moreCampaigns.length > 0);

  const loadMore = useCallback(async () => {
    if (!businessProfile?.id || loadingMore || !hasMore) return;
    const allSoFar = sortCampaignsByCreatedAt([...baseCampaigns, ...moreCampaigns]);
    const last = allSoFar[allSoFar.length - 1];
    if (!last?.createdAt) return;

    setLoadingMore(true);
    try {
      const campaignsRef = collection(db, 'campaign');
      const q = query(
        campaignsRef,
        where('companyId', '==', businessProfile.id),
        orderBy('createdAt', 'desc'),
        startAfter(last.createdAt),
        limit(LOAD_MORE_PAGE_SIZE)
      );
      const snapshot = await getDocs(q);
      const next = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setMoreCampaigns((prev) => [...prev, ...next]);
      if (next.length < LOAD_MORE_PAGE_SIZE) setHasMore(false);
    } catch (err) {
      console.error('Error loading more campaigns:', err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [businessProfile?.id, loadingMore, hasMore, baseCampaigns, moreCampaigns]);

  // Reset "load more" state when workspace/context changes
  useEffect(() => {
    setMoreCampaigns([]);
    setHasMore(true);
  }, [businessProfile?.id]);

  // Keep local state in sync with context (for any code that still reads campaigns)
  useEffect(() => {
    if (Array.isArray(contextCampaigns)) {
      setCampaigns(sortCampaignsByCreatedAt(contextCampaigns));
    } else if (!user || !businessProfile?.id) {
      setCampaigns([]);
    }
  }, [contextCampaigns, user, businessProfile?.id]);

  // Show skeleton until we have a company and campaigns have loaded (avoids flash of empty state)
  if (!businessProfile || !campaignsReady) {
    return <CampaignsSkeleton />;
  }

  return (
    <div className="gch-content-width gch-page">
      <header className="mb-3 sm:mb-6">
        <div className="flex flex-col gap-2 sm:gap-5">
          {/* Title row - always inline, compact icon button on mobile */}
          <div className="flex items-center justify-between">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight gch-text-primary">
              Campaigns
            </h1>
            <button
              className="btn-admin-primary shrink-0 inline-flex items-center justify-center gap-2 p-2.5 rounded-xl sm:rounded-[var(--admin-cta-radius)] sm:px-5 sm:py-2.5"
              onClick={() => navigate('/studio/new')}
            >
              <PlusIcon className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">Create campaign</span>
            </button>
          </div>
          {(allCampaigns.length > 0 || statusFilter !== STATUS_FILTER_ALL) ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full">
              {/* Filter pills with edge-fade scroll affordance */}
              <div className="min-w-0 sm:flex-1">
                <ScrollableTabBar className="-mx-4 sm:mx-0" innerClassName="px-4 sm:px-0">
                  <div className="inline-flex items-center gap-2 w-max sm:w-fit">
                    <div className="inline-flex rounded-[var(--admin-card-radius)] overflow-hidden bg-white/5 border border-[var(--admin-border)]" role="group" aria-label="Filter by status">
                      {[STATUS_FILTER_ALL, STATUS_FILTER_DRAFT, STATUS_FILTER_PUBLISHED, STATUS_FILTER_PAUSED, STATUS_FILTER_COMPLETED].filter((filter) => filter !== STATUS_FILTER_PAUSED || statusCounts[filter] > 0 || statusFilter === filter).map((filter) => (
                        <button
                          key={filter}
                          type="button"
                          onClick={() => setStatusFilter(filter)}
                          title={filter === STATUS_FILTER_ALL ? 'Draft, published, and paused' : undefined}
                          className={`whitespace-nowrap px-2.5 py-1.5 text-xs font-semibold transition-all duration-200 border-r border-[var(--admin-border)] last:border-r-0 ${
                            statusFilter === filter
                              ? 'gch-text-primary bg-white/10'
                              : 'gch-text-secondary hover:gch-text-primary hover:bg-white/5'
                          }`}
                        >
                          {filter === STATUS_FILTER_ALL ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                          <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
                            statusFilter === filter
                              ? 'bg-white/15 gch-text-primary'
                              : 'bg-white/5 gch-text-muted'
                          }`}>{statusCounts[filter]}</span>
                        </button>
                      ))}
                    </div>
                    {displayCampaigns.length > 0 && (
                      <div className="inline-flex rounded-[var(--admin-card-radius)] overflow-hidden bg-white/5 border border-[var(--admin-border)] sm:hidden" role="group" aria-label="Sort by">
                        <button
                          type="button"
                          onClick={() => setSortBy(SORT_BY_CREATED)}
                          className={`px-2.5 py-1.5 text-xs font-semibold transition-all duration-200 border-r border-[var(--admin-border)] ${
                            sortBy === SORT_BY_CREATED ? 'gch-text-primary bg-white/10' : 'gch-text-secondary hover:gch-text-primary hover:bg-white/5'
                          }`}
                        >
                          Date
                        </button>
                        <button
                          type="button"
                          onClick={() => setSortBy(SORT_BY_PLAYS)}
                          className={`px-2.5 py-1.5 text-xs font-semibold transition-all duration-200 ${
                            sortBy === SORT_BY_PLAYS ? 'gch-text-primary bg-white/10' : 'gch-text-secondary hover:gch-text-primary hover:bg-white/5'
                          }`}
                        >
                          Plays
                        </button>
                      </div>
                    )}
                  </div>
                </ScrollableTabBar>
              </div>
              {displayCampaigns.length > 0 && (
                <div className="hidden sm:flex items-center shrink-0">
                  <div className="inline-flex rounded-[var(--admin-card-radius)] overflow-hidden bg-white/5 border border-[var(--admin-border)]" role="group" aria-label="Sort by">
                    <button
                      type="button"
                      onClick={() => setSortBy(SORT_BY_CREATED)}
                      className={`px-2.5 py-1.5 text-xs font-semibold transition-all duration-200 border-r border-[var(--admin-border)] ${
                        sortBy === SORT_BY_CREATED ? 'gch-text-primary bg-white/10' : 'gch-text-secondary hover:gch-text-primary hover:bg-white/5'
                      }`}
                    >
                      Date
                    </button>
                    <button
                      type="button"
                      onClick={() => setSortBy(SORT_BY_PLAYS)}
                      className={`px-2.5 py-1.5 text-xs font-semibold transition-all duration-200 ${
                        sortBy === SORT_BY_PLAYS ? 'gch-text-primary bg-white/10' : 'gch-text-secondary hover:gch-text-primary hover:bg-white/5'
                      }`}
                    >
                      Plays
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm font-medium gch-text-secondary">
              Manage your campaigns and track their performance
            </p>
          )}
        </div>
      </header>

      {/* Empty state — header only */}
      {displayCampaigns.length === 0 ? (
        <div className="min-h-[280px] gch-card flex items-center justify-center">
          <div className="p-10 text-center w-full max-w-md mx-auto">
            <div
              className="mx-auto mb-4 w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'var(--admin-accent-light, rgba(124,58,237,0.12))' }}
              aria-hidden="true"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" style={{ color: 'var(--admin-accent)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.898 20.624L16.5 21.75l-.398-1.126a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.179-.398a2.25 2.25 0 001.423-1.423L16.5 15.75l.398 1.179a2.25 2.25 0 001.423 1.423L19.5 18.75l-1.179.398a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold gch-text-primary tracking-tight">
              {statusFilter === STATUS_FILTER_ALL && 'No campaigns yet'}
              {statusFilter === STATUS_FILTER_DRAFT && 'No draft campaigns'}
              {statusFilter === STATUS_FILTER_PUBLISHED && 'No published campaigns'}
              {statusFilter === STATUS_FILTER_PAUSED && 'No paused campaigns'}
              {statusFilter === STATUS_FILTER_COMPLETED && 'No completed campaigns'}
            </h3>
            <p className="mt-2 text-sm gch-text-secondary">
              {statusFilter === STATUS_FILTER_ALL && 'Create your first branded game in a couple of minutes.'}
              {statusFilter !== STATUS_FILTER_ALL && 'They\'ll show up here once you publish or change status.'}
            </p>
            {statusFilter === STATUS_FILTER_PUBLISHED && allCampaigns.some((c) => normalizeCampaignStatus(c.status) === STATUS_FILTER_DRAFT) && (
              <button
                onClick={() => setStatusFilter(STATUS_FILTER_DRAFT)}
                className="btn-admin-outline btn-admin-md mt-6"
              >
                View drafts to publish
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <motion.div
            className="grid grid-cols-12 gap-3 sm:gap-6"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            {sortedDisplayCampaigns.map((campaign) => (
              <CampaignsCard key={campaign.id} campaign={campaign} onClick={() => navigate(`/studio/campaign/${campaign.id}`, { state: { from: '/studio/campaigns' } })} />
            ))}
          </motion.div>
          {canLoadMore && statusFilter === STATUS_FILTER_ALL && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="btn-admin-outline btn-admin-md"
              >
                {loadingMore ? 'Loading…' : 'Load more campaigns'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
